'use client'

import { Helmet } from 'react-helmet-async'
import Header from './LandingPage/Layout/Header'
import Footer from './LandingPage/Layout/Footer'
import WhatsAppButton from './LandingPage/UI/WhatsAppButton'
import { DISPLAY_PHONE, getWhatsAppUrl } from '../../config/contact'

export default function RefundPolicy() {
  return (
    <div className="bg-background-50">
      <Helmet>
        <title>Refund Policy | ScanMaadi</title>
        <meta name="description" content="Refund Policy for ScanMaadi — understand our refund terms for subscriptions, QR products, and payment issues on our digital menu platform." />
      </Helmet>

      <Header />

      <main className="pt-28 pb-16 sm:pb-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          {/* Page Title */}
          <div className="mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-text-800">
              Refund Policy
            </h1>
            <p className="mt-4 text-text-500 text-sm">
              Effective Date: 1 January 2025 &nbsp;|&nbsp; Last Updated: 11 February 2026
            </p>
          </div>

          <div className="prose-custom space-y-8 text-text-600 leading-relaxed">
            {/* 1. Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">1. Introduction</h2>
              <p>
                This Refund Policy applies to all purchases made on the ScanMaadi platform
                ("<strong>Platform</strong>"), operated by Montivo Labs LLP ("<strong>Company</strong>",
                "<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>"), a limited liability
                partnership registered under the Limited Liability Partnership Act, 2008, with its
                registered office at Bangalore, Karnataka, India.
              </p>
              <p className="mt-4">
                This Refund Policy is part of and should be read together with our{' '}
                <a href="/terms" className="text-primary-500 hover:text-accent-500 underline">Terms of Service</a>.
                By making a purchase on the Platform, you agree to the terms of this Refund Policy.
              </p>
              <p className="mt-4">
                We are committed to fair and transparent business practices. Please read this policy
                carefully before making a purchase to understand your rights and our obligations regarding
                refunds.
              </p>
            </section>

            {/* 2. Subscription Refund Policy */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">2. Subscription Refund Policy</h2>
              <p>
                The ScanMaadi Digital Menu Service is offered as an annual subscription at{' '}
                <strong>₹999 per year</strong> (Indian Rupees Nine Hundred and Ninety-Nine only).
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">2.1 Non-Refundable</h3>
              <p>
                All subscription purchases are <strong>strictly non-refundable</strong>. Once a subscription
                is purchased and activated, no refund will be issued for any reason, including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Early cancellation or discontinuation of use before the subscription period ends</li>
                <li>Unused portion of the subscription period</li>
                <li>Dissatisfaction with the Service (we encourage you to contact our support team to resolve any issues)</li>
                <li>Change of business plans or closure of your food establishment</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">2.2 Grace Period</h3>
              <p>
                Upon expiry of your subscription, you will receive a <strong>30-day grace period</strong>{' '}
                during which the Service will continue to remain accessible at no additional charge. This
                grace period allows you time to renew your subscription without any disruption to your
                digital menu service.
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">2.3 Statutory Exception</h3>
              <p>
                The non-refundable nature of subscriptions does not override any mandatory refund rights
                available to you under applicable Indian law, including the Consumer Protection Act, 2019.
              </p>
            </section>

            {/* 3. Physical QR Product Policy */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">3. Physical QR Product Policy</h2>
              <p>
                ScanMaadi offers physical QR products including <strong>QR Stands</strong> (₹250 each) and{' '}
                <strong>QR Stickers</strong> (₹25 each). These products are custom-manufactured with your
                unique QR code printed on them.
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">3.1 Non-Returnable</h3>
              <p>
                Due to the customised nature of our QR products, all physical products are{' '}
                <strong>non-returnable and non-refundable</strong> once manufactured and dispatched. Each
                QR stand and sticker is printed with your specific QR code, making it unique to your
                business and unsuitable for resale or reuse.
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">3.2 Damaged or Defective Products</h3>
              <p>
                If you receive a QR product that is damaged during transit or has a manufacturing defect,
                we will provide a <strong>replacement at no additional cost</strong>, or issue a refund at the
                Company's discretion. To claim a replacement or refund for a damaged/defective product:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>You must notify us within <strong>48 hours</strong> of receiving the product.</li>
                <li>You must provide <strong>photographic evidence</strong> of the damage or defect (clear images of the product and packaging).</li>
                <li>The product must not have been tampered with or damaged by the Vendor after delivery.</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">3.3 Wrong Product Delivered</h3>
              <p>
                If you receive a product that does not match your order (e.g., wrong quantity, wrong product
                type, or QR code printed with incorrect information due to our error), we will dispatch the
                correct product at no additional cost. Please contact us within <strong>48 hours</strong>{' '}
                of delivery to report the issue.
              </p>
            </section>

            {/* 4. Payment Failures and Double Payments */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">4. Payment Failures and Double Payments</h2>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">4.1 Payment Deducted but Order Not Confirmed</h3>
              <p>
                In rare cases, your payment may be deducted but the order may not be confirmed on the
                Platform due to a technical issue. In such cases:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>The amount will be <strong>automatically reversed</strong> to your UPI account within 5-7 business days.</li>
                <li>If the auto-reversal does not occur within 7 business days, please contact our support team with your transaction details (UPI reference number, date, and amount).</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">4.2 Double or Duplicate Payments</h3>
              <p>
                If you are charged more than once for the same order, the excess amount will be refunded
                to your original UPI payment method within <strong>7-10 business days</strong>. Please
                contact our support team with details of both transactions so we can expedite the refund.
              </p>
            </section>

            {/* 5. How to Request a Refund */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">5. How to Request a Refund</h2>
              <p>
                If you believe you are eligible for a refund (for damaged products, payment errors, or
                duplicate charges), please follow these steps:
              </p>

              <div className="mt-4 space-y-4">
                <div className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-4 mt-0.5">1</span>
                  <div>
                    <p className="font-semibold text-text-700">Contact our support team</p>
                    <p className="text-text-600 mt-1">
                      Reach out via email at{' '}
                      <a href="mailto:hello@scanmaadi.in" className="text-primary-500 hover:text-accent-500 underline">hello@scanmaadi.in</a>{' '}
                      or via{' '}
                      <a href={getWhatsAppUrl('I would like to request a refund')} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:text-accent-500 underline">WhatsApp</a>.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-4 mt-0.5">2</span>
                  <div>
                    <p className="font-semibold text-text-700">Provide the following details</p>
                    <ul className="list-disc pl-6 space-y-1 mt-1 text-text-600">
                      <li>Your registered phone number</li>
                      <li>Order details (order ID, date of purchase, items purchased)</li>
                      <li>Reason for the refund request</li>
                      <li>Photographic evidence (for damaged/defective product claims)</li>
                      <li>UPI transaction reference number (for payment failures)</li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-4 mt-0.5">3</span>
                  <div>
                    <p className="font-semibold text-text-700">Await our response</p>
                    <p className="text-text-600 mt-1">
                      We will acknowledge your refund request within <strong>24 hours</strong> and
                      communicate our decision within <strong>7 business days</strong> of receiving
                      your complete request with all required details.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* 6. Refund Processing */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">6. Refund Processing</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Approved refunds will be processed within <strong>7-10 business days</strong> from the date of approval.</li>
                <li>Refunds will be credited to the <strong>original UPI payment method</strong> used for the purchase.</li>
                <li>The Company is not responsible for any delays in the refund caused by your payment provider, bank, or UPI service.</li>
                <li>You will receive a confirmation via email or WhatsApp once the refund has been initiated.</li>
              </ul>
            </section>

            {/* 7. Non-Refundable Circumstances */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">7. Non-Refundable Circumstances</h2>
              <p>The following purchases and situations are strictly non-refundable:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>All subscription purchases, regardless of usage or remaining subscription period.</li>
                <li>Physical QR products (stands and stickers) that have been customised and dispatched, unless they are damaged or defective on arrival (see Section 3.2).</li>
                <li>Refund requests found to be fraudulent, abusive, or made in bad faith.</li>
                <li>Purchases made by Vendors whose accounts have been terminated for violation of the{' '}
                  <a href="/terms" className="text-primary-500 hover:text-accent-500 underline">Terms of Service</a>.
                </li>
              </ul>
            </section>

            {/* 8. Consumer Protection */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">8. Consumer Protection</h2>
              <p>
                This Refund Policy does not limit or override any rights available to you under the
                Consumer Protection Act, 2019, or any other applicable consumer protection laws in India.
                Where this policy conflicts with your statutory rights, your statutory rights shall prevail.
              </p>
              <p className="mt-4">
                If you have a consumer complaint that has not been satisfactorily resolved through our
                support channels, you may approach the appropriate Consumer Disputes Redressal Commission
                in accordance with the Consumer Protection Act, 2019.
              </p>
            </section>

            {/* 9. Changes to This Policy */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">9. Changes to This Policy</h2>
              <p>
                We may update this Refund Policy from time to time. Any material changes will be communicated
                to registered Vendors via email or through a prominent notice on the Platform. The updated
                policy will apply to purchases made after the effective date of the revised policy. Purchases
                made before the change will be governed by the policy in effect at the time of purchase.
              </p>
            </section>

            {/* 10. Contact Information */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">10. Contact Us</h2>
              <p>
                For any questions or concerns regarding this Refund Policy, or to initiate a refund request,
                please contact us:
              </p>
              <div className="mt-4 bg-background-100 rounded-xl p-6 border border-background-200">
                <p className="font-semibold text-text-800">Montivo Labs LLP</p>
                <p className="text-text-600 mt-1">(Operating as ScanMaadi)</p>
                <div className="mt-4 space-y-2 text-text-600">
                  <p>Registered Office: Bangalore, Karnataka, India</p>
                  <p>
                    Email:{' '}
                    <a href="mailto:hello@scanmaadi.in" className="text-primary-500 hover:text-accent-500 underline">
                      hello@scanmaadi.in
                    </a>
                  </p>
                  <p>Phone: {DISPLAY_PHONE}</p>
                  <p>
                    WhatsApp:{' '}
                    <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:text-accent-500 underline">
                      Message us on WhatsApp
                    </a>
                  </p>
                </div>
              </div>
            </section>

            {/* Closing */}
            <section className="mt-12 pt-8 border-t border-background-200">
              <p className="text-text-500 text-sm">
                By making a purchase on ScanMaadi, you acknowledge that you have read and understood this
                Refund Policy and agree to its terms.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  )
}
